import { forgotPasswordSchema } from '@insightai/auth'
import { Button, Field, Input } from '@insightai/ui'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { config } from '../../config'
import { mockRequestPasswordReset } from '../../auth/mockAuth'
import { useZodForm } from '../../auth/useZodForm'
import { AuthLayout } from './AuthLayout'

/**
 * M00-22 — forgot password.
 *
 * The confirmation is deliberately non-committal: "if that email is registered".
 * Saying "we've sent you an email" confirms which addresses have accounts, which
 * is account enumeration on an unauthenticated endpoint. The same wording is
 * shown whether the address exists or the request failed.
 */
export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false)
  const form = useZodForm(forgotPasswordSchema, { email: '' })

  const onSubmit = form.handleSubmit(async (values) => {
    if (config.prototypeMode) {
      await mockRequestPasswordReset()
    } else {
      await fetch(`${config.apiBaseUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: values.email }),
      }).catch(() => undefined)
    }
    setSent(true)
  })

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a link to choose a new password."
      footer={
        <Link to="/auth/login" className="text-text-link underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      {sent ? (
        <div role="status" className="flex flex-col gap-3">
          <p className="text-body-sm text-text-primary">
            If that email is registered, a reset link is on its way.
          </p>
          <p className="text-caption text-text-secondary">
            The link expires in 30 minutes. Check your spam folder if it does not arrive.
          </p>
          {/* No email is sent in prototype mode, which would leave the reset screen
              reachable only by hand-typing a token. This is the emailed link. */}
          {config.prototypeMode && (
            <p className="text-caption text-text-secondary">
              Prototype mode sends no email.{' '}
              <Link
                to="/auth/reset-password?token=demo-reset-token"
                className="text-text-link underline underline-offset-4"
              >
                Open the reset link
              </Link>{' '}
              instead.
            </p>
          )}
        </div>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <Field label="Work email" required error={form.errors.email}>
            <Input
              type="email"
              autoComplete="email"
              autoFocus
              value={form.values.email}
              onChange={(event) => form.setField('email', event.target.value)}
            />
          </Field>
          <Button type="submit" loading={form.submitting}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
